/**
 * Month grid interactions (노션식):
 * - 빈 날짜 클릭 → 셀 안 인라인 빠른 추가 (Enter 저장·연속 입력, Tab 다음 날,
 *   Ctrl+Enter 상세 모달, Esc 닫기). 드래그로 기간을 잡으면 그 기간으로 열린다.
 * - Ctrl/⌘/Shift+클릭 → 다중 선택, 하단 액션 바(색상·완료·복제·삭제).
 *   선택된 바를 끌면 전부 같이 이동. Delete 로 일괄 삭제, Ctrl+A 전체 선택,
 *   Ctrl+D 복제, Alt+←/→ 하루 이동, Esc 선택 해제.
 * - 바 드래그 이동 / 가장자리 리사이즈.
 * 모든 변경은 calendar-history 의 스택을 거쳐 Ctrl+Z / Ctrl+Shift+Z 로 되돌린다.
 */
import {
    api, createCommand, deleteCommand, patchCommand, composite,
    commit, record, showToast, labels, fmtCount, refreshGrid,
} from './calendar-history.js';
import { showContextMenu } from './context-menu.js';
import { bindQuickTime } from './quick-time.js';
import { appChoose } from './confirm-dialog.js';
import { splitScope, sendScoped, deleteScoped } from './calendar-scope.js';
import { PHONE_MEDIA } from './phone.js';

function localISODate(d) {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}
function addDays(ds, n) {
    const d = new Date(ds + 'T00:00:00');
    d.setDate(d.getDate() + n);
    return localISODate(d);
}
function diffDays(a, b) {
    return Math.round((new Date(b + 'T00:00:00') - new Date(a + 'T00:00:00')) / 86400000);
}
function csv(s) {
    return (s || '').split(',').filter(Boolean);
}
function inTextField(target) {
    if (!target) return false;
    const tag = (target.tagName || '').toUpperCase();
    if (tag === 'INPUT') return !/^(checkbox|radio|button|submit|reset|range|color|file)$/i.test(target.type || 'text');
    return tag === 'TEXTAREA' || tag === 'SELECT' || !!target.isContentEditable;
}
// 터치 기기/좁은 화면: 드래그 선택·인라인 추가 대신 날짜 칸 누르기(모바일은 그날 목록)만 쓴다.
function isTouchLayout() {
    return window.matchMedia(PHONE_MEDIA + ', (pointer: coarse)').matches;
}
function overlayOpen() {
    const m = document.getElementById('calendarModal');
    const p = document.getElementById('calProjModal');
    return (m && m.style.display === 'flex') || (p && p.style.display === 'flex');
}

// Build the create-form body for a copy of a bar (복제 / bulk duplicate).
function duplicateBody(bar) {
    const d = bar.dataset;
    const p = new URLSearchParams();
    p.set('title', d.title || '');
    p.set('start_date', d.start);
    p.set('end_date', d.end);
    if (d.allday === '1') {
        p.set('all_day', 'on');
    } else {
        p.set('start_time', d.stime || '09:00');
        p.set('end_time', d.etime || '10:00');
    }
    p.set('color', d.color || 'c1');
    if (d.shareAll === '1') p.set('share_all', '1');
    p.set('memo', d.memo || '');
    p.set('calendar_id', d.calid || '');
    p.set('parent_id', d.parent || '');
    csv(d.shareUsers).forEach((v) => p.append('share_users', v));
    csv(d.shareDepts).forEach((v) => p.append('share_depts', v));
    csv(d.shareGroups).forEach((v) => p.append('share_groups', v));
    return p;
}

export function setupCalendarGrid() {
    const grid = document.getElementById('calGrid');
    if (!grid || grid.dataset.spaBound) return; // 뷰 전환으로 되살아난 루트는 이미 묶여 있다
    grid.dataset.spaBound = '1';

    // ── Selection ────────────────────────────────────────────────────
    const selected = new Set();
    // 선택 바는 리전(selbar) 안이라 종류 전환·새로고침마다 새 노드다 (AQ11) — 쓸 때마다 찾고, 화면의 그리드일 때만 만진다
    // (뷰 전환으로 풀에 들어간 그리드는 id 가 없다).
    const active = () => document.getElementById('calGrid') === grid;

    function barsFor(id) {
        return grid.querySelectorAll('.cal-bar[data-id="' + CSS.escape(id) + '"]');
    }
    // First bar element per id (the dataset is identical across week splits).
    function firstBar(id) {
        return grid.querySelector('.cal-bar[data-id="' + CSS.escape(id) + '"]');
    }
    function applySelection() {
        // Drop ids that disappeared (deleted / moved out of the month).
        Array.from(selected).forEach((id) => {
            if (!firstBar(id)) selected.delete(id);
        });
        grid.querySelectorAll('.cal-bar').forEach((b) => {
            b.classList.toggle('selected', selected.has(b.dataset.id));
        });
        const selBar = active() ? document.getElementById('calSelBar') : null;
        if (selBar) {
            selBar.hidden = selected.size === 0;
            const selCount = document.getElementById('calSelCount');
            if (selCount) selCount.textContent = fmtCount(labels().selected, selected.size);
        }
    }
    function clearSelection() {
        if (!selected.size) return;
        selected.clear();
        applySelection();
    }
    function toggleSelect(id) {
        if (selected.has(id)) selected.delete(id);
        else selected.add(id);
        applySelection();
    }
    function selectAll() {
        grid.querySelectorAll('.cal-bar').forEach((b) => selected.add(b.dataset.id));
        applySelection();
    }
    document.addEventListener('calendar:refreshed', applySelection);
    window.calendarGridClearSelection = clearSelection;

    // Modifier-click on a bar toggles selection instead of opening the modal.
    // Capture phase so the inline onclick (openCalendarEvent) never fires.
    grid.addEventListener('click', (e) => {
        // 막대의 체크 링: 패널을 열지 않고 완료/미완료 (2026-09-18)
        const chk = e.target.closest('.cal-bar-check');
        if (chk && !(e.ctrlKey || e.metaKey || e.shiftKey)) {
            e.stopPropagation();
            e.preventDefault();
            toggleBarDone(chk.closest('.cal-bar'));
            return;
        }
        const bar = e.target.closest('.cal-bar');
        if (bar && (e.ctrlKey || e.metaKey || e.shiftKey)) {
            e.stopPropagation();
            e.preventDefault();
            toggleSelect(bar.dataset.id);
            return;
        }
        if (!bar && selected.size) {
            // 빈 곳 클릭: 선택만 해제 (빠른 추가는 다음 클릭에서)
            e.stopPropagation();
            e.preventDefault();
            clearSelection();
            return;
        }
        if (bar && selected.size) clearSelection();
    }, true);

    // ── Bulk actions ─────────────────────────────────────────────────
    function selectedBars() {
        return Array.from(selected).map(firstBar).filter(Boolean);
    }
    // 반복 인스턴스가 섞이면 한 번 묻는다 (BA1, calendar-scope.js): 「이 일정만」은 평소 길,
    // 「이후 모두」·「전체」는 보통 일정은 평소대로 · 반복은 시리즈마다 한 번 series_scope 로 (실행 취소 밖).
    function bulkDelete() {
        const bars = selectedBars();
        const mine = bars.filter((b) => b.dataset.isowner === '1');
        if (!mine.length) {
            showToast(labels().ownerOnly, { error: true });
            return;
        }
        splitScope(mine, 'delete').then((sp) => {
            if (!sp) return;
            const L = labels();
            clearSelection();
            if (sp.plain.length) {
                const cmd = composite(sp.plain.map((b) => deleteCommand(b.dataset.id)));
                const msg = sp.plain.length === 1 ? L.deleted : fmtCount(L.bulkDeleted, sp.plain.length);
                commit(cmd, msg).then(() => {
                    if (mine.length < bars.length) showToast(L.ownerOnly);
                });
            }
            if (sp.scoped.length) deleteScoped(sp.scoped, sp.scope);
        });
    }
    // ask = 반복 범위를 물을 편집인가 (완료·숨김은 언제나 그 행만 — 묻지 않는다)
    function bulkPatch(fieldsFor, msgPattern, ask) {
        const bars = selectedBars();
        if (!bars.length) return;
        (ask ? splitScope(bars, 'edit') : Promise.resolve({ scope: 'this', plain: bars, scoped: [] })).then((sp) => {
            if (!sp) return;
            if (sp.plain.length) {
                const cmds = sp.plain.map((b) => {
                    const f = fieldsFor(b);
                    return patchCommand(b.dataset.id, f.before, f.after);
                });
                const L = labels();
                commit(composite(cmds), sp.plain.length === 1 ? null : fmtCount(msgPattern || L.bulkUpdated, sp.plain.length));
            }
            if (sp.scoped.length) sendScoped(sp.scoped, sp.scope, (b) => fieldsFor(b).after);
        });
    }
    function bulkColor(color) {
        bulkPatch((b) => ({ before: { color: b.dataset.color || 'c1' }, after: { color } }), null, true);
    }
    function bulkDone() {
        const bars = selectedBars();
        const allDone = bars.every((b) => b.dataset.done === '1');
        bulkPatch((b) => ({ before: { done: b.dataset.done === '1' ? '1' : '0' }, after: { done: allDone ? '0' : '1' } }));
    }
    // 막대 하나의 완료 토글 (링 클릭): 낙관적으로 칠하고 토스트(실행 취소)로 알린다.
    function toggleBarDone(bar) {
        if (!bar || !bar.dataset.id) return;
        const was = bar.dataset.done === '1';
        bar.dataset.done = was ? '0' : '1';
        bar.classList.toggle('done', !was);
        const L = labels();
        commit(patchCommand(bar.dataset.id, { done: was ? '1' : '0' }, { done: was ? '0' : '1' }), was ? L.notDone : L.done)
            .catch(() => { bar.dataset.done = was ? '1' : '0'; bar.classList.toggle('done', was); showToast(L.failed, { error: true }); });
    }
    // 숨기기: 선택 전부가 숨김이면 해제, 아니면 숨김 (완료 토글과 같은 규칙).
    function bulkHide() {
        const bars = selectedBars();
        const allHidden = bars.every((b) => b.dataset.hidden === '1');
        bulkPatch((b) => ({ before: { hidden: b.dataset.hidden === '1' ? '1' : '0' }, after: { hidden: allHidden ? '0' : '1' } }), labels().bulkHidden);
    }
    function bulkShift(days) {
        const L = labels();
        bulkPatch((b) => ({
            before: { start_date: b.dataset.start, end_date: b.dataset.end },
            after: { start_date: addDays(b.dataset.start, days), end_date: addDays(b.dataset.end, days) },
        }), L.bulkMoved, true);
    }
    function bulkDuplicate() {
        const bars = selectedBars();
        if (!bars.length) return;
        const L = labels();
        bars.reduce((p, b) => p.then((acc) => api.create(duplicateBody(b)).then((ev) => acc.concat([ev]))), Promise.resolve([]))
            .then((created) => {
                const cmd = composite(created.map((ev) => createCommand(ev.id)));
                clearSelection();
                return record(cmd, bars.length === 1 ? L.duplicated : fmtCount(L.bulkUpdated, bars.length));
            })
            .catch(() => showToast(L.failed, { error: true }));
    }
    document.addEventListener('click', (e) => {
        const btn = e.target.closest('#calSelBar button');
        if (!btn || !active()) return;
        if (btn.dataset.color) bulkColor(btn.dataset.color);
        else if (btn.dataset.act === 'done') bulkDone();
        else if (btn.dataset.act === 'hide') bulkHide();
        else if (btn.dataset.act === 'dup') bulkDuplicate();
        else if (btn.dataset.act === 'delete') bulkDelete();
        else if (btn.dataset.act === 'clear') clearSelection();
    });

    // ── Quick add (inline) ───────────────────────────────────────────
    let quick = null; // { box, input, start, end }

    function closeQuickAdd() {
        if (!quick) return;
        quick.box.remove();
        quick = null;
    }
    function quickTitleLabel(start, end) {
        if (start === end) return '';
        const s = new Date(start + 'T00:00:00');
        const e = new Date(end + 'T00:00:00');
        return (s.getMonth() + 1) + '/' + s.getDate() + ' – ' + (e.getMonth() + 1) + '/' + e.getDate();
    }
    function saveQuick(then) {
        if (!quick) return Promise.resolve();
        const title = quick.input.value.trim();
        const q = quick;
        if (!title) {
            if (then) then();
            return Promise.resolve();
        }
        q.input.disabled = true;
        const body = new URLSearchParams();
        body.set('title', title);
        body.set('start_date', q.start);
        body.set('end_date', q.end);
        body.set('all_day', 'on');
        body.set('color', grid.dataset.quickColor || 'c1');
        if (grid.dataset.calId) body.set('calendar_id', grid.dataset.calId); // 프로젝트 허브의 월·주 뷰: 그 프로젝트 작업으로
        if (q.qt) q.qt.applyTo(body); // 감지된 날짜·시각·반복이 있으면 제목을 떼고 그대로 (quick-time.js)
        const L = labels();
        return api.create(body)
            .then((ev) => {
                closeQuickAdd();
                if (ev && ev.series) { // 반복 (BA5 a): 시리즈 생성은 실행 취소 밖 — 패널 저장과 같은 규칙
                    showToast(fmtCount(grid.dataset.msgSeriesCreated || '반복 일정 %d개를 만들었습니다', ev.created_count || 1));
                    return refreshGrid();
                }
                return record(createCommand(ev.id), L.created);
            })
            .then(() => { if (then) then(); })
            .catch(() => {
                q.input.disabled = false;
                q.input.focus();
                showToast(L.failed, { error: true });
            });
    }
    function openQuickAdd(start, end, preset) {
        // 읽기 패널이 열려 있으면 닫고 진행 (배경이 없어 클릭이 통과한다)
        const m = document.getElementById('calendarModal');
        if (m && m.style.display === 'flex' && m.classList.contains('is-read') && window.closeCalendarModal) window.closeCalendarModal();
        if (isTouchLayout()) {
            window.openCalendarCreate(start, end);
            return;
        }
        closeQuickAdd();
        clearSelection();
        const cell = grid.querySelector('.cal-day[data-date="' + start + '"]');
        if (!cell) {
            window.openCalendarCreate(start, end);
            return;
        }
        const L = labels();
        const box = document.createElement('div');
        box.className = 'cal-quick';
        const range = document.createElement('div');
        range.className = 'cal-quick-range';
        range.textContent = quickTitleLabel(start, end || start);
        if (range.textContent) box.appendChild(range);
        const input = document.createElement('input');
        input.type = 'text';
        input.maxLength = 200;
        input.placeholder = L.quickPh;
        input.autocomplete = 'off';
        input.value = preset || '';
        box.appendChild(input);
        // 시간 감지 칩 (quick-time.js): "3시 회의" → 15:00–16:00 · 회의, ✕ 로 끈다
        const timeChip = document.createElement('div');
        box.appendChild(timeChip);
        // 날짜·반복도 감지한다 (AZ3 · BA5 a): 상대 날짜는 오늘에서, "28일" 은 누른 칸의 달에서
        const qt = bindQuickTime(input, timeChip, { offLabel: grid.dataset.quickTimeOff, base: start, today: grid.dataset.today || '', weekdays: grid.dataset.weekdays, fromFmt: grid.dataset.quickFrom, weekStart: grid.dataset.weekStart });
        const hint = document.createElement('div');
        hint.className = 'cal-quick-hint';
        hint.textContent = L.quickHint;
        const detail = document.createElement('button');
        detail.type = 'button';
        detail.className = 'cal-quick-detail';
        detail.textContent = grid.dataset.detailLabel || '상세…';
        detail.addEventListener('click', () => {
            const title = input.value, s = quick.start, en = quick.end;
            closeQuickAdd();
            window.openCalendarCreate(s, en);
            const t = document.getElementById('calTitle');
            if (t) { t.value = title; t.dispatchEvent(new Event('input', { bubbles: true })); } // 제목의 날짜·시간 칩 (AZ4)
        });
        hint.appendChild(detail);
        box.appendChild(hint);
        // 셀 클릭/드래그 핸들러로 번지지 않게 (mousedown 이 create 드래그를 시작한다)
        ['mousedown', 'click'].forEach((t) => box.addEventListener(t, (e) => e.stopPropagation()));
        cell.appendChild(box);
        quick = { box, input, start, end: end || start, qt };
        input.focus();

        input.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                closeQuickAdd();
                return;
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                if (e.ctrlKey || e.metaKey) {
                    // 상세 모달로 이어 쓰기 — 입력한 제목을 들고 간다.
                    const title = input.value;
                    const s = quick.start, en = quick.end;
                    closeQuickAdd();
                    window.openCalendarCreate(s, en);
                    const t = document.getElementById('calTitle');
                    if (t) t.value = title;
                    return;
                }
                const s = quick.start, en = quick.end;
                saveQuick(() => openQuickAdd(s, en)); // 연속 입력
                return;
            }
            if (e.key === 'Tab') {
                e.preventDefault();
                const next = addDays(quick.start, e.shiftKey ? -1 : 1);
                saveQuick(() => openQuickAdd(next, next));
            }
        });
        input.addEventListener('blur', () => {
            // 바깥 클릭: 입력한 게 있으면 저장 (노션 방식), 없으면 그냥 닫는다.
            setTimeout(() => {
                if (!quick || quick.input !== input) return;
                if (document.activeElement === input) return;
                if (input.value.trim()) saveQuick();
                else closeQuickAdd();
            }, 120);
        });
    }
    window.openCalendarQuickAdd = openQuickAdd;

    // ── Drag: create range / move (multi) / resize ───────────────────
    function dayAt(x, y) {
        const el = document.elementFromPoint(x, y);
        const day = el && el.closest ? el.closest('.cal-day') : null;
        if (day && day.dataset.date) return day.dataset.date;
        const days = grid.querySelectorAll('.cal-day');
        for (let i = 0; i < days.length; i += 1) {
            const r = days[i].getBoundingClientRect();
            if (x >= r.left && x < r.right && y >= r.top && y < r.bottom) return days[i].dataset.date || null;
        }
        return null;
    }
    function highlightRange(a, b) {
        const lo = a < b ? a : b, hi = a < b ? b : a;
        grid.querySelectorAll('.cal-day').forEach((d) => {
            const ds = d.dataset.date;
            d.classList.toggle('drag-sel', !!ds && ds >= lo && ds <= hi);
        });
    }
    function clearHighlight() {
        grid.querySelectorAll('.cal-day.drag-sel').forEach((d) => d.classList.remove('drag-sel'));
    }

    let mode = null; // 'create' | 'move' | 'resize' | 'resize-l'
    let anchor = null, lastDate = null, dragBar = null, moved = false;
    let ghost = null, grabX = 0, grabY = 0, downX = 0, downY = 0;

    function updateGhost(e) {
        if (mode !== 'move' || !dragBar) return;
        if (!ghost) {
            if (Math.abs(e.clientX - downX) < 4 && Math.abs(e.clientY - downY) < 4) return;
            const r = dragBar.getBoundingClientRect();
            grabX = downX - r.left;
            grabY = downY - r.top;
            ghost = dragBar.cloneNode(true);
            ghost.classList.add('cal-bar-ghost');
            ghost.style.cssText = 'position:fixed; left:0; top:0; width:' + r.width + 'px; height:' + r.height + 'px; margin:0;';
            document.body.appendChild(ghost);
            dragBar.classList.add('dragging');
        }
        ghost.style.left = (e.clientX - grabX) + 'px';
        ghost.style.top = (e.clientY - grabY) + 'px';
    }
    function removeGhost() {
        if (ghost) {
            ghost.remove();
            ghost = null;
        }
    }
    // Which bars a move applies to: the whole selection when the grabbed
    // bar is part of it, otherwise just that bar.
    function moveTargets(bar) {
        if (selected.has(bar.dataset.id)) return selectedBars();
        return [bar];
    }

    let swallowClick = false;
    grid.addEventListener('click', (e) => {
        if (swallowClick) {
            swallowClick = false;
            e.stopPropagation();
            e.preventDefault();
        }
    }, true);

    grid.addEventListener('mousedown', (e) => {
        if (e.button !== 0 || isTouchLayout()) return;
        if (e.target.closest('.cal-quick')) return;
        if (e.target.closest('.cal-bar-check')) return; // 링 클릭은 드래그가 아니다
        const resize = e.target.closest('.cal-bar-resize');
        const resizeL = e.target.closest('.cal-bar-resize-l');
        const bar = e.target.closest('.cal-bar');
        if (resize && bar) {
            mode = 'resize'; dragBar = bar; anchor = bar.dataset.start; moved = false;
            e.preventDefault();
            return;
        }
        if (resizeL && bar) {
            mode = 'resize-l'; dragBar = bar; anchor = bar.dataset.end; moved = false;
            e.preventDefault();
            return;
        }
        if (bar) {
            mode = 'move'; dragBar = bar; anchor = dayAt(e.clientX, e.clientY); moved = false;
            downX = e.clientX; downY = e.clientY;
            return;
        }
        const day = e.target.closest('.cal-day');
        if (day && day.dataset.date && !day.classList.contains('pad')) {
            closeQuickAdd();
            mode = 'create'; anchor = day.dataset.date; lastDate = anchor; moved = false;
            highlightRange(anchor, anchor);
            e.preventDefault();
        }
    });

    document.addEventListener('mousemove', (e) => {
        if (!mode) return;
        updateGhost(e);
        const ds = dayAt(e.clientX, e.clientY);
        if (!ds || ds === lastDate) return;
        lastDate = ds;
        moved = true;
        if (mode === 'create') {
            highlightRange(anchor, ds);
        } else if (mode === 'move' && dragBar && anchor) {
            const delta = diffDays(anchor, ds);
            if (delta !== 0) {
                dragBar.classList.add('dragging');
                moveTargets(dragBar).forEach((b) => barsFor(b.dataset.id).forEach((el) => el.classList.add('dragging')));
                highlightRange(addDays(dragBar.dataset.start, delta), addDays(dragBar.dataset.end, delta));
            }
        } else if (mode === 'resize' && dragBar) {
            dragBar.classList.add('dragging');
            highlightRange(dragBar.dataset.start, ds < dragBar.dataset.start ? dragBar.dataset.start : ds);
        } else if (mode === 'resize-l' && dragBar) {
            dragBar.classList.add('dragging');
            highlightRange(ds > dragBar.dataset.end ? dragBar.dataset.end : ds, dragBar.dataset.end);
        }
    });

    function endDrag() {
        grid.querySelectorAll('.cal-bar.dragging').forEach((b) => b.classList.remove('dragging'));
        mode = null; dragBar = null; anchor = null; lastDate = null; moved = false;
        removeGhost();
        clearHighlight();
    }

    document.addEventListener('mouseup', (e) => {
        if (!mode) return;
        const ds = dayAt(e.clientX, e.clientY);
        const m = mode, bar = dragBar, a = anchor, didMove = moved;
        endDrag();
        if (didMove) swallowClick = true;
        const L = labels();

        if (m === 'create' && ds && a) {
            const lo = a < ds ? a : ds, hi = a < ds ? ds : a;
            if (didMove) {
                openQuickAdd(lo, hi);
            }
            return;
        }
        // 반복 인스턴스 하나의 드래그: 범위를 묻고, 「이 일정만」이 아니면 실행 취소 밖에서
        // 바로 보낸다 (반복 스펙 §6.2). 취소하면 낙관적으로 옮긴 막대를 새로고침으로 되돌린다.
        const SL = grid.dataset;
        const askScope = () => appChoose({
            title: SL.scopeTitle || '반복 일정을 어떻게 바꿀까요?',
            choices: [
                { value: 'this', label: SL.scopeThis || '이 일정만', primary: true },
                { value: 'following', label: SL.scopeFollowing || '이후 모두' },
                { value: 'all', label: SL.scopeAll || '전체' },
            ],
        });
        const seriesAwarePatch = (target, before, after, label) => {
            if (!target.dataset.series) { commit(patchCommand(target.dataset.id, before, after), label); return; }
            askScope().then((scope) => {
                if (scope === null) { refreshGrid(); return; }
                if (scope === 'this') { commit(patchCommand(target.dataset.id, before, after), label); return; }
                api.patch(target.dataset.id, Object.assign({}, after, { series_scope: scope }))
                    .then((r) => { showToast(fmtCount(SL.msgSeriesChanged || '일정 %d개를 바꿨습니다', ((r && r.affected) || 0) + 1)); return refreshGrid(); })
                    .catch(() => { showToast(L.failed, { error: true }); refreshGrid(); });
            });
        };
        if (m === 'move' && bar && didMove && ds && a) {
            const delta = diffDays(a, ds);
            if (delta === 0) return;
            const targets = moveTargets(bar);
            if (targets.length === 1) {
                const b = targets[0];
                seriesAwarePatch(b, { start_date: b.dataset.start, end_date: b.dataset.end },
                    { start_date: addDays(b.dataset.start, delta), end_date: addDays(b.dataset.end, delta) }, L.moved);
                return;
            }
            // 여러 막대: 반복 인스턴스가 섞이면 한 번 묻는다 (BA1) — 취소하면 제자리로
            splitScope(targets, 'edit').then((sp) => {
                if (!sp) { refreshGrid(); return; }
                if (sp.plain.length) {
                    const cmds = sp.plain.map((b) => patchCommand(b.dataset.id,
                        { start_date: b.dataset.start, end_date: b.dataset.end },
                        { start_date: addDays(b.dataset.start, delta), end_date: addDays(b.dataset.end, delta) }));
                    commit(composite(cmds), fmtCount(L.bulkMoved, sp.plain.length));
                }
                if (sp.scoped.length) sendScoped(sp.scoped, sp.scope, (b) => ({ start_date: addDays(b.dataset.start, delta), end_date: addDays(b.dataset.end, delta) }));
            });
            return;
        }
        if (m === 'resize' && bar && ds) {
            const end = ds < bar.dataset.start ? bar.dataset.start : ds;
            if (end !== bar.dataset.end) {
                seriesAwarePatch(bar, { end_date: bar.dataset.end }, { end_date: end }, L.resized);
            }
            return;
        }
        if (m === 'resize-l' && bar && ds) {
            const start = ds > bar.dataset.end ? bar.dataset.end : ds;
            if (start !== bar.dataset.start) {
                seriesAwarePatch(bar, { start_date: bar.dataset.start }, { start_date: start }, L.resized);
            }
        }
    });

    // ── 더블클릭: 빈 날짜는 상세 패널, 바는 편집 모드 ──
    grid.addEventListener('dblclick', (e) => {
        if (e.target.closest('.cal-bar-check')) return;
        const bar = e.target.closest('.cal-bar');
        if (bar) {
            e.preventDefault();
            if (window.openCalendarEvent) window.openCalendarEvent(bar, { edit: true });
            return;
        }
        const day = e.target.closest('.cal-day');
        if (day && day.dataset.date && !day.classList.contains('pad')) {
            e.preventDefault();
            closeQuickAdd();
            window.openCalendarCreate(day.dataset.date, day.dataset.date);
        }
    });

    // ── 우클릭 메뉴: 날짜 칸 / 바 ──
    grid.addEventListener('contextmenu', (e) => {
        const L = labels();
        const M = grid.dataset;
        const bar = e.target.closest('.cal-bar');
        if (bar) {
            e.preventDefault();
            const list = selected.has(bar.dataset.id) ? selectedBars() : [bar];
            if (!selected.has(bar.dataset.id)) { clearSelection(); }
            showContextMenu(e.clientX, e.clientY, [
                { label: M.menuOpen || '열기', onClick: () => window.openCalendarEvent(bar) },
                { label: M.menuEdit || '편집', onClick: () => window.openCalendarEvent(bar, { edit: true }) },
                { separator: true },
                { label: M.menuDone || '완료/미완료', onClick: () => { list.forEach((b) => selected.add(b.dataset.id)); applySelection(); bulkDone(); } },
                { label: M.menuHide || '숨기기', onClick: () => { list.forEach((b) => selected.add(b.dataset.id)); applySelection(); bulkHide(); } },
                { colors: ['c1', 'c2', 'c3', 'c4', 'c5', 'c6'], onColor: (c) => { list.forEach((b) => selected.add(b.dataset.id)); applySelection(); bulkColor(c); } },
                { label: M.menuDup || '복제', hint: 'Ctrl+D', onClick: () => { list.forEach((b) => selected.add(b.dataset.id)); applySelection(); bulkDuplicate(); } },
                { separator: true },
                // 반복 인스턴스가 있으면 bulkDelete 가 범위를 한 번 묻는다 (반복 스펙 §6.2 · BA1)
                { label: M.menuDelete || '삭제', hint: 'Del', danger: true, disabled: !list.some((b) => b.dataset.isowner === '1'), onClick: () => {
                    list.forEach((b) => selected.add(b.dataset.id)); applySelection(); bulkDelete();
                } },
            ]);
            return;
        }
        const day = e.target.closest('.cal-day');
        if (day && day.dataset.date && !day.classList.contains('pad')) {
            e.preventDefault();
            const ds = day.dataset.date;
            showContextMenu(e.clientX, e.clientY, [
                { label: M.menuQuick || '빠른 추가', hint: 'Click', onClick: () => openQuickAdd(ds, ds) },
                { label: M.menuNew || '상세 일정 추가', hint: 'Dbl-click', onClick: () => { closeQuickAdd(); window.openCalendarCreate(ds, ds); } },
            ]);
        }
        void L;
    });

    // ── Keyboard ─────────────────────────────────────────────────────
    document.addEventListener('keydown', (e) => {
        if (grid.id !== 'calGrid') return; // 목록 뷰로 넘어가 풀에 숨은 동안엔 쉼
        if (e.key === 'Escape' && mode) {
            // 진행 중인 드래그 취소 — 저장 없이 원상 복귀.
            endDrag();
            swallowClick = true;
            e.stopImmediatePropagation();
            return;
        }
        if (overlayOpen() || inTextField(e.target)) return;
        const mod = e.ctrlKey || e.metaKey;
        if (e.key === 'Escape') {
            if (quick) { closeQuickAdd(); return; }
            if (selected.size) { clearSelection(); }
            return;
        }
        if (!selected.size) {
            if (mod && e.key.toLowerCase() === 'a' && !e.shiftKey) {
                e.preventDefault();
                selectAll();
            }
            return;
        }
        if (e.key === 'Delete' || e.key === 'Backspace') {
            e.preventDefault();
            bulkDelete();
        } else if (mod && e.key.toLowerCase() === 'd') {
            e.preventDefault();
            bulkDuplicate();
        } else if (mod && e.key.toLowerCase() === 'a') {
            e.preventDefault();
            selectAll();
        } else if (e.altKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
            e.preventDefault();
            bulkShift(e.key === 'ArrowLeft' ? -1 : 1);
        }
    }, true);

}
